
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { 
  ChevronDown, 
  ArrowRight, 
  Search, 
  Heart, 
  FileText, 
  Home,
  MapPin,
  Calendar,
  MessageSquare,
  HelpCircle,
  Star,
  BookOpen
} from "lucide-react";
import { AuthButtons } from "./AuthButtons";
import { popularLocations, otherLocations, resourceItems, companyItems } from "./navData";
import { useAuth } from "@/components/auth/AuthProvider";

interface MobileMenuProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function MobileMenu({ isOpen, onClose }: MobileMenuProps) {
  const [findHomeOpen, setFindHomeOpen] = useState(false);
  const [shortlistOpen, setShortlistOpen] = useState(false);
  const [journeyOpen, setJourneyOpen] = useState(false);
  const [resourcesOpen, setResourcesOpen] = useState(false);
  const [companyOpen, setCompanyOpen] = useState(false);
  const { user } = useAuth();

  if (!isOpen) return null;

  const userName = user?.user_metadata?.first_name || null;

  return (
    <div className="lg:hidden bg-white border-t shadow-lg fixed top-[72px] left-0 right-0 z-40 max-h-[calc(100vh-72px)] overflow-y-auto">
      <div className="container py-6">
        <div className="space-y-5">
          {/* Personalized Welcome - Only for logged in users */}
          {user && (
            <div className="bg-grandpal-beige/50 p-4 rounded-lg mb-2">
              <h3 className="text-grandpal-green font-medium text-lg">
                {`Hi ${userName || 'there'}`}
              </h3>
              <div className="mt-3 space-y-2">
                <Link 
                  to="/dashboard" 
                  className="flex items-center text-gray-700 hover:text-grandpal-green transition-colors group"
                  onClick={onClose}
                >
                  <span className="flex-1">My Dashboard</span>
                  <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link 
                  to="/waitlist" 
                  className="flex items-center text-gray-700 hover:text-grandpal-green transition-colors group"
                  onClick={onClose}
                >
                  <span className="flex-1">My Waitlist Applications</span>
                  <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </div>
          )}

          {/* Find a Home Section */}
          <div>
            <button 
              onClick={() => setFindHomeOpen(!findHomeOpen)}
              className="flex items-center justify-between py-3 border-b w-full text-left"
            >
              <div className="flex items-center">
                <Home className="h-5 w-5 text-grandpal-green mr-3" />
                <h3 className="font-medium text-gray-800 font-lora">Find a Home</h3>
              </div>
              <ChevronDown 
                className={`h-5 w-5 text-grandpal-green transition-transform duration-200 ${
                  findHomeOpen ? 'rotate-180' : 'rotate-0'
                }`}
              />
            </button>
            {findHomeOpen && (
              <div className="py-4 pl-4 space-y-4 animate-fade-in">
                <h4 className="text-xs uppercase tracking-wide text-grandpal-green font-medium mb-2">Search options</h4>
                <div className="space-y-3">
                  <Link
                    to="/nursing-homes"
                    className="flex items-center gap-2 py-1.5 text-gray-700 hover:text-grandpal-green transition-colors"
                    onClick={onClose}
                  >
                    <Search className="h-4 w-4 text-grandpal-green" />
                    <span>Search all homes</span>
                  </Link>
                  <Link
                    to="/nursing-homes?map=true"
                    className="flex items-center gap-2 py-1.5 text-gray-700 hover:text-grandpal-green transition-colors"
                    onClick={onClose}
                  >
                    <MapPin className="h-4 w-4 text-grandpal-green" />
                    <span>Find homes by location</span>
                  </Link>
                </div>

                <h4 className="text-xs uppercase tracking-wide text-grandpal-green font-medium my-2">Popular locations</h4>
                <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                  {popularLocations.map((item) => (
                    <Link
                      key={item.title}
                      to={item.path}
                      className="block py-1.5 text-gray-700 hover:text-grandpal-green transition-colors"
                      onClick={onClose}
                    >
                      {item.title}
                    </Link>
                  ))}
                  {otherLocations.map((item) => (
                    <Link
                      key={item.title}
                      to={item.path}
                      className="block py-1.5 text-gray-700 hover:text-grandpal-green transition-colors"
                      onClick={onClose}
                    >
                      {item.title}
                    </Link>
                  ))}
                </div>
                
                <Link
                  to="/waitlist"
                  className="flex items-center text-grandpal-green font-medium group mt-2"
                  onClick={onClose}
                >
                  Join a Waitlist
                  <ArrowRight className="ml-1.5 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            )}
          </div>

          {/* Shortlist & Compare Section */}
          <div>
            <button 
              onClick={() => setShortlistOpen(!shortlistOpen)}
              className="flex items-center justify-between py-3 border-b w-full text-left"
            >
              <div className="flex items-center">
                <Heart className="h-5 w-5 text-grandpal-green mr-3" />
                <h3 className="font-medium text-gray-800 font-lora">Shortlist & Compare</h3>
              </div>
              <ChevronDown 
                className={`h-5 w-5 text-grandpal-green transition-transform duration-200 ${
                  shortlistOpen ? 'rotate-180' : 'rotate-0'
                }`}
              />
            </button>
            {shortlistOpen && (
              <div className="py-4 pl-4 space-y-3 animate-fade-in">
                <Link
                  to="/dashboard?tab=saved"
                  className="flex items-center gap-2 py-1.5 text-gray-700 hover:text-grandpal-green transition-colors"
                  onClick={onClose}
                >
                  <Heart className="h-4 w-4 text-grandpal-green" />
                  <span>View saved homes</span>
                </Link>
                <Link
                  to="/nursing-homes/compare"
                  className="flex items-center gap-2 py-1.5 text-gray-700 hover:text-grandpal-green transition-colors"
                  onClick={onClose}
                >
                  <Star className="h-4 w-4 text-grandpal-green" />
                  <span>Compare homes side-by-side</span>
                </Link>
              </div>
            )}
          </div>

          {/* Your Journey Section */}
          <div>
            <button 
              onClick={() => setJourneyOpen(!journeyOpen)}
              className="flex items-center justify-between py-3 border-b w-full text-left"
            >
              <div className="flex items-center">
                <FileText className="h-5 w-5 text-grandpal-green mr-3" />
                <h3 className="font-medium text-gray-800 font-lora">Your Journey</h3>
              </div>
              <ChevronDown 
                className={`h-5 w-5 text-grandpal-green transition-transform duration-200 ${
                  journeyOpen ? 'rotate-180' : 'rotate-0'
                }`}
              />
            </button>
            {journeyOpen && (
              <div className="py-4 pl-4 space-y-3 animate-fade-in">
                <Link
                  to="/dashboard?tab=applications"
                  className="flex items-center gap-2 py-1.5 text-gray-700 hover:text-grandpal-green transition-colors"
                  onClick={onClose}
                >
                  <FileText className="h-4 w-4 text-grandpal-green" />
                  <span>Application progress</span>
                </Link>
                <Link
                  to="/dashboard?tab=visits"
                  className="flex items-center gap-2 py-1.5 text-gray-700 hover:text-grandpal-green transition-colors"
                  onClick={onClose}
                >
                  <Calendar className="h-4 w-4 text-grandpal-green" />
                  <span>Book visits</span>
                </Link>
              </div>
            )}
          </div>

          {/* Resources Section */}
          <div>
            <button 
              onClick={() => setResourcesOpen(!resourcesOpen)}
              className="flex items-center justify-between py-3 border-b w-full text-left"
            >
              <div className="flex items-center">
                <BookOpen className="h-5 w-5 text-grandpal-green mr-3" />
                <h3 className="font-medium text-gray-800 font-lora">Resources</h3>
              </div>
              <ChevronDown 
                className={`h-5 w-5 text-grandpal-green transition-transform duration-200 ${
                  resourcesOpen ? 'rotate-180' : 'rotate-0'
                }`}
              />
            </button>
            {resourcesOpen && (
              <div className="py-4 pl-4 space-y-3 animate-fade-in">
                <h4 className="text-xs uppercase tracking-wide text-grandpal-green font-medium mb-2">Decision Support Tools</h4>
                {resourceItems.map((item) => (
                  <div key={item.title} className="flex items-start py-2">
                    <div className="mr-3 mt-0.5 bg-grandpal-green-light p-2 rounded-md text-grandpal-green">
                      {item.icon}
                    </div>
                    <div>
                      <Link
                        to={item.path}
                        className="block font-medium text-gray-700 hover:text-grandpal-green transition-colors"
                        onClick={onClose}
                      >
                        {item.title}
                      </Link>
                      <p className="text-sm text-grandpal-gray mt-1">{item.description}</p>
                    </div>
                  </div>
                ))}
                <Link
                  to="/calculator"
                  className="flex items-center gap-2 py-1.5 text-gray-700 hover:text-grandpal-green transition-colors"
                  onClick={onClose}
                >
                  <FileText className="h-4 w-4 text-grandpal-green" />
                  <span>Cost Estimator</span>
                </Link>
              </div>
            )}
          </div>

          {/* Company Section */}
          <div>
            <button 
              onClick={() => setCompanyOpen(!companyOpen)}
              className="flex items-center justify-between py-3 border-b w-full text-left"
            >
              <div className="flex items-center">
                <MapPin className="h-5 w-5 text-grandpal-green mr-3" />
                <h3 className="font-medium text-gray-800 font-lora">Company</h3>
              </div>
              <ChevronDown 
                className={`h-5 w-5 text-grandpal-green transition-transform duration-200 ${
                  companyOpen ? 'rotate-180' : 'rotate-0'
                }`}
              />
            </button>
            {companyOpen && (
              <div className="py-4 pl-4 space-y-3 animate-fade-in">
                {companyItems.map((item) => (
                  <div key={item.title} className="flex items-start py-2">
                    <div className="mr-3 mt-0.5 bg-grandpal-green-light p-2 rounded-md text-grandpal-green">
                      {item.icon}
                    </div>
                    <div>
                      <Link
                        to={item.path}
                        className="block font-medium text-gray-700 hover:text-grandpal-green transition-colors"
                        onClick={onClose}
                      >
                        {item.title}
                      </Link>
                      <p className="text-sm text-grandpal-gray mt-1">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Guide link */}
          <div>
            <Link
              to="/guides"
              className="flex items-center py-3 border-b font-medium text-gray-800 hover:text-grandpal-green transition-colors font-lora"
              onClick={onClose}
            >
              <BookOpen className="h-5 w-5 text-grandpal-green mr-3" />
              <span>Guides</span>
            </Link>
          </div>

          {/* Support Section */}
          <div>
            <Link
              to="/contact"
              className="flex items-center py-3 border-b font-medium text-gray-800 hover:text-grandpal-green transition-colors font-lora"
              onClick={onClose}
            >
              <MessageSquare className="h-5 w-5 text-grandpal-green mr-3" />
              <span>Chat with Support</span>
            </Link>
          </div>

          {/* Waitlist CTA Section */}
          <div className="bg-gradient-to-br from-white to-grandpal-beige p-5 rounded-lg my-5 relative overflow-hidden border border-grandpal-beige-dark shadow-sm">
            <div className="relative z-10">
              <div className="flex gap-4 items-start">
                <div>
                  <h3 className="text-grandpal-green text-lg font-bold mb-3 font-lora">Join a waitlist for free</h3>
                  <p className="text-gray-700 text-sm mb-4 max-w-[280px]">
                    Sign up to a waitlist to boost your odds of finding the right nursing home quicker.
                  </p>
                  <Link
                    to="/waitlist"
                    className="flex items-center text-white font-medium group bg-grandpal-green px-4 py-2.5 rounded-md shadow-sm hover:bg-grandpal-green-dark transition-colors"
                    onClick={onClose}
                  >
                    Join a Waitlist
                    <ArrowRight className="ml-2 h-4 w-4 transform group-hover:translate-x-1 transition-transform" />
                  </Link>
                </div>
                <div className="flex-none">
                  <img 
                    src="/lovable-uploads/b4d6b12f-537c-4411-8f7c-9770afaa4de2.png" 
                    alt="Coffee and cookies" 
                    className="h-24 w-auto object-contain"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Auth buttons */}
          <div className="mt-5 pb-2">
            <AuthButtons />
          </div>
        </div>
      </div>
    </div>
  );
}
